import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, Mail } from "lucide-react";
import ApiService from "../../services/api";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import { WhiteLabelButton } from "../../utils/whiteLabelUtils";

type ForgotPasswordStep = "email" | "verify" | "success";

interface ForgotPasswordData {
  require_2fa_verification: boolean;
  two_fa_method: string;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { primaryColor, isWhiteLabel, logoUrl, brandName, aboutLink } =
    useWhiteLabel();
  const [step, setStep] = useState<ForgotPasswordStep>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [twoFaMethod, setTwoFaMethod] = useState("");

  const validateEmail = (value: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const validatePassword = (value: string) => {
    return value.trim().length >= 6;
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailError("");

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      const data = await ApiService.forgotPassword(email);

      if (!data.success) {
        throw new Error(data.message || "Failed to send reset instructions");
      }

      setTwoFaMethod(data.data.two_fa_method);
      setStep("verify");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to send reset instructions"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setPasswordError("");
    setConfirmPasswordError("");

    const otpValue = otp.join("");
    let hasError = false;

    if (otpValue.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    if (!validatePassword(newPassword)) {
      setPasswordError("Password must be at least 6 characters");
      hasError = true;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);

    try {
      const data = await ApiService.forgotPasswordVerifyOTP(
        email,
        newPassword,
        otpValue
      );

      if (!data.success) {
        throw new Error(data.message || "Failed to reset password");
      }

      setStep("success");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid code or failed to reset password"
      );
      setOtp(["", "", "", "", "", ""]); // Clear OTP inputs
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  // Step 1: Email Input
  if (step === "email") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 bg-white p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
            {/* Logo */}
            <div className="text-center">
              <div
                // className="mx-auto h-14 w-14 sm:h-16 sm:w-16 bg-blue-100 rounded-lg flex items-center justify-center"
                className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Lock
                  // className="h-8 w-8 text-blue-600"
                  className="h-8 w-8"
                  style={{ color: primaryColor }}
                />
              </div>
              <h2 className="mt-4 sm:mt-6 text-xl sm:text-2xl font-bold text-gray-900">
                Forgot password?
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-gray-600">
                No worries, we'll send you reset instructions.
              </p>
            </div>

            {/* Form */}
            <form
              className="max-w-md mx-auto space-y-6 p-4"
              onSubmit={handleForgotPassword}
            >
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError && validateEmail(e.target.value)) {
                        setEmailError("");
                      }
                    }}
                    className={`appearance-none relative block w-full px-3 py-2.5 sm:py-3 border ${
                      emailError ? "border-red-500" : "border-gray-300"
                    } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-black-900 focus:border-black-700 focus:z-10 text-sm sm:text-base`}
                    placeholder="Enter your email"
                    autoComplete="email"
                  />
                  {emailError && (
                    <p className="text-red-600 text-xs sm:text-sm mt-1">
                      {emailError}
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-xs sm:text-sm text-center">
                  {error}
                </div>
              )}

              <div>
                <WhiteLabelButton
                  type="submit"
                  disabled={isLoading}
                  // className="group relative w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  className="group relative w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Sending..." : "Reset password"}
                </WhiteLabelButton>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  // className="inline-flex items-center text-xs sm:text-sm text-gray-600 hover:text-gray-900 font-medium"
                  className="inline-flex items-center text-xs sm:text-sm font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to log in
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: OTP Verification and Password Reset
  if (step === "verify") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-6 bg-white p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
            <div className="text-center">
              <div
                // className="mx-auto h-14 w-14 sm:h-16 sm:w-16 bg-blue-100 rounded-lg flex items-center justify-center"
                className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Mail
                  // className="h-8 w-8 text-blue-600"
                  className="h-8 w-8"
                  style={{ color: primaryColor }}
                />
              </div>
              <h2 className="mt-4 text-xl sm:text-2xl font-bold text-gray-900">
                Check your email
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-gray-600">
                We sent a verification code to{" "}
                <span className="font-medium">{email}</span>
              </p>
            </div>

            <form
              className="space-y-4 sm:space-y-6"
              onSubmit={handlePasswordReset}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <div className="flex justify-center space-x-2">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min={0}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      // className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:gray-blue-300"
                      maxLength={1}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    New Password
                  </label>
                  <div className="relative w-full">
                    <input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      required
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (passwordError && validatePassword(e.target.value)) {
                          setPasswordError("");
                        }
                      }}
                      className={`block w-full px-3 py-2.5 sm:py-3 border ${
                        passwordError ? "border-red-500" : "border-gray-300"
                      } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm sm:text-base pr-10`}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                    <div className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-500">
                      {showNewPassword ? (
                        <EyeOff
                          size={18}
                          onClick={() => setShowNewPassword(false)}
                        />
                      ) : (
                        <Eye
                          size={18}
                          onClick={() => setShowNewPassword(true)}
                        />
                      )}
                    </div>
                  </div>
                  {passwordError && (
                    <p className="text-red-600 text-xs sm:text-sm mt-1">
                      {passwordError}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm Password
                  </label>
                  <div className="relative w-full">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (
                          confirmPasswordError &&
                          e.target.value === newPassword
                        ) {
                          setConfirmPasswordError("");
                        }
                      }}
                      className={`block w-full px-3 py-2.5 sm:py-3 border ${
                        confirmPasswordError
                          ? "border-red-500"
                          : "border-gray-300"
                      } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm sm:text-base pr-10`}
                      style={
                        isWhiteLabel
                          ? {
                              // "--tw-ring-color": primaryColor,
                              borderColor: confirmPasswordError
                                ? "#ef4444"
                                : undefined,
                            }
                          : {}
                      }
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                    <div className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-500">
                      {showConfirmPassword ? (
                        <EyeOff
                          size={18}
                          onClick={() => setShowConfirmPassword(false)}
                        />
                      ) : (
                        <Eye
                          size={18}
                          onClick={() => setShowConfirmPassword(true)}
                        />
                      )}
                    </div>
                  </div>
                  {confirmPasswordError && (
                    <p className="text-red-600 text-xs sm:text-sm mt-1">
                      {confirmPasswordError}
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-xs sm:text-sm text-center">
                  {error}
                </div>
              )}

              <div>
                <WhiteLabelButton
                  type="submit"
                  disabled={isLoading}
                  // className="w-full bg-blue-600 text-white py-2.5 sm:py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  className="w-full text-white py-2.5 sm:py-3 px-4 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Resetting..." : "Reset password"}
                </WhiteLabelButton>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setStep("email")}
                  // className="inline-flex items-center text-xs sm:text-sm text-gray-600 hover:text-gray-900 font-medium"
                  className="inline-flex items-center text-xs sm:text-sm font-medium hover:underline"
                  style={
                    isWhiteLabel
                      ? { color: primaryColor }
                      : { color: "#374151" }
                  }
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to email entry
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Success Screen
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200 text-center">
          <div>
            <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center">
              {isWhiteLabel ? (
                <img
                  src={logoUrl}
                  alt={`${brandName} Logo`}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/logo.svg";
                  }}
                />
              ) : (
                <div
                  className="w-full h-full rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: "#dcfce7" }}
                >
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              )}
            </div>
            <h2 className="mt-4 text-xl sm:text-2xl font-bold text-gray-900">
              Password reset successful!
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              Your password has been successfully reset. You can now log in with
              your new password.
            </p>
          </div>

          <div>
            <WhiteLabelButton
              onClick={handleBackToLogin}
              // className="w-full bg-blue-600 text-white py-2.5 sm:py-3 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              className="w-full text-white py-2.5 sm:py-3 px-4 rounded-md-blue-700 transition-colors font-medium"
            >
              Back to login
            </WhiteLabelButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
